import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import AuthShell from "./AuthShell";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Endpoint always returns 200 to prevent email enumeration — we mirror
      // that with a "check your inbox" confirmation regardless of what the
      // backend saw.
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch {
      // Should be rare — network only. Still show a "check inbox" state so
      // the presence/absence of the account isn't revealed.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthShell
        title="Check your inbox"
        subtitle={`If an account exists for ${email || "that email"}, we sent a password reset link.`}
        testId="forgot-sent-page"
        footer={<Link to="/login" className="text-purple-700 font-semibold hover:underline" data-testid="forgot-back-to-login">Back to sign-in</Link>}
      >
        <div className="text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="forgot-sent-notice">
          The link is valid for the next hour. Didn&apos;t get anything? Check your spam folder or try again in a few minutes.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter the email address you used to sign up and we'll send you a reset link."
      testId="forgot-page"
      footer={<Link to="/login" className="text-purple-700 font-semibold hover:underline" data-testid="forgot-back-link">Back to sign-in</Link>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="forgot-email"
            required
            className="w-full border-2 border-slate-200 focus:border-black rounded-xl px-4 py-3 outline-none transition"
            placeholder="you@example.com"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="forgot-submit"
          className="w-full inline-flex items-center justify-center gap-2 iti-btn-primary px-4 py-3 font-semibold disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send reset link"}
        </button>
      </form>
    </AuthShell>
  );
}
